import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

interface CompressorTempData {
  time: string;
  stat_type: string;
  sensor_kit_serial_number: string;
  temp_c: number;
  predicted_max_temp_c?: number;
  predicted_critical_max_temp_c?: number;
  power_w?: number;
}

interface CompressorTempChartSimpleProps {
  data: CompressorTempData[];
  timeRange: string;
  selectedStatType: string;
  aggregationType?: 'daily' | 'hourly';
  dataMode?: 'aggregated' | 'raw';
  highlightedDetectionTime?: Date | null;
  chartTitle?: string; // Optional title for download filename
  showDownloadButton?: boolean; // Whether to show the download button in the chart
}

export interface CompressorTempChartSimpleRef {
  download: () => void;
}

const CompressorTempChartSimple = forwardRef<CompressorTempChartSimpleRef, CompressorTempChartSimpleProps>(({ 
  data,
  timeRange,
  selectedStatType,
  aggregationType = 'daily',
  dataMode = 'aggregated',
  highlightedDetectionTime = null,
  chartTitle,
  showDownloadButton = true
}, ref) => {
  const isMobile = useIsMobile();
  const chartRef = useRef<any>(null);

  const handleDownload = () => {
    if (chartRef.current) {
      const chartInstance = chartRef.current.getEchartsInstance();
      const url = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      });
      
      const link = document.createElement('a');
      link.download = `${chartTitle || 'chart'}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = url;
      link.click();
    }
  };

  // Expose download function via ref
  useImperativeHandle(ref, () => ({
    download: handleDownload
  }));

  const filteredData = data.filter(d => d.stat_type === selectedStatType);
  const isRawMode = dataMode === 'raw';
  
  const chartData = filteredData.map(d => {
    // Handle both date formats:
    // - "2025-09-26" (daily aggregation) 
    // - "2025-09-26 14:34:33.000 UTC" (cycle data)
    let timestamp;
    const timeStr = d.time;
    if (timeStr.includes('T') || timeStr.includes('Z')) {
      // Already ISO format e.g. "2025-09-26T14:00:00.000Z"
      timestamp = new Date(timeStr).getTime();
    } else if (timeStr.includes(' ') && timeStr.includes('UTC')) {
      // Athena format e.g. "2025-09-26 14:34:33.000 UTC"
      const isoTime = timeStr.replace(' UTC', 'Z').replace(' ', 'T');
      timestamp = new Date(isoTime).getTime();
    } else {
      // Daily format e.g. "2025-09-26"
      timestamp = new Date(timeStr + 'T12:00:00Z').getTime();
    }
    
    // Convert temp_c to number (it comes as a string from Athena)
    const tempCelsius = typeof d.temp_c === 'string' ? parseFloat(d.temp_c) : (d.temp_c || null);
    const tempFahrenheit = tempCelsius !== null ? tempCelsius * 9/5 + 32 : null;
    
    // Convert power_w to number
    const powerW = typeof d.power_w === 'string' ? parseFloat(d.power_w) : (d.power_w || null);
    
    return {
      x: timestamp,
      y: tempFahrenheit,
      temp_c: tempCelsius,
      power_w: powerW,
      sensor_kit: d.sensor_kit_serial_number
    };
  }).filter(d => d.temp_c !== null || d.power_w !== null); // Filter out rows with no data

  const statLabels = {
    'min': 'Minimum',
    'p5': '5th Percentile',
    'median': 'Median',
    'p95': '95th Percentile',
    'max': 'Daily Maximum',
    'compressor_inlet_min': 'Compressor Inlet Minimum',
    'compressor_inlet_median': 'Compressor Inlet Median',
    'compressor_outlet_max': 'Compressor Outlet Maximum',
    'compressor_outlet_median': 'Compressor Outlet Median'
  };

  const statColors = {
    'min': '#3b82f6',
    'p5': '#8b5cf6',
    'median': '#001F5C', // Thalo navy blue
    'p95': '#f59e0b',
    'max': '#001F5C', // Thalo navy blue (--thalo-blue: hsl(234, 99%, 27%))
    'compressor_inlet_min': '#001F5C', // Thalo navy blue
    'compressor_inlet_median': '#001F5C', // Thalo navy blue
    'compressor_outlet_max': '#001F5C', // Thalo navy blue
    'compressor_outlet_median': '#001F5C' // Thalo navy blue
  };

  const selectedStat = {
    label: statLabels[selectedStatType as keyof typeof statLabels] || selectedStatType,
    color: statColors[selectedStatType as keyof typeof statColors] || '#ef4444'
  };

  // Get the most recent predicted max temperature (warning) and critical max temperature
  const mostRecentData = filteredData.length > 0 ? filteredData[filteredData.length - 1] : null;
  const mostRecentRefTempRaw = mostRecentData?.predicted_max_temp_c ?? null;
  const mostRecentCriticalTempRaw = mostRecentData?.predicted_critical_max_temp_c ?? null;
  
  // Convert to number if it's a string
  const mostRecentRefTemp = mostRecentRefTempRaw 
    ? (typeof mostRecentRefTempRaw === 'string' ? parseFloat(mostRecentRefTempRaw) : mostRecentRefTempRaw)
    : null;
  
  const mostRecentCriticalTemp = mostRecentCriticalTempRaw
    ? (typeof mostRecentCriticalTempRaw === 'string' ? parseFloat(mostRecentCriticalTempRaw) : mostRecentCriticalTempRaw)
    : null;
  
  // Convert to Fahrenheit
  // predicted_max_temp_c is the warning value, predicted_critical_max_temp_c is the critical value
  const warningTempF = mostRecentRefTemp ? (mostRecentRefTemp * 9/5 + 32) : null;
  const criticalTempF = mostRecentCriticalTemp ? (mostRecentCriticalTemp * 9/5 + 32) : null;

  const tempData = chartData.filter(d => d.y !== null);
  const powerData = chartData.filter(d => d.power_w !== null);
  
  const minTemp = tempData.length > 0 ? tempData.reduce((m, d) => Math.min(m, d.y!), Infinity) : 0;
  const maxTemp = tempData.length > 0 ? tempData.reduce((m, d) => Math.max(m, d.y!), -Infinity) : 100;
  const minPower = powerData.length > 0 ? powerData.reduce((m, d) => Math.min(m, d.power_w!), Infinity) : 0;
  const maxPower = powerData.length > 0 ? powerData.reduce((m, d) => Math.max(m, d.power_w!), -Infinity) : 1000;

  // Determine if this is an inlet or outlet chart
  const isInlet = selectedStatType.includes('inlet');
  const isOutlet = selectedStatType.includes('outlet');
  const sensorType = isInlet ? 'Inlet' : isOutlet ? 'Outlet' : 'Outlet'; // Default to outlet for backward compatibility
  
  // Freezing point constant (32°F)
  const FREEZING_TEMP_F = 32;

  // Build chart title - avoid duplication of "Compressor Inlet/Outlet"
  const getChartTitle = () => {
    if (isRawMode) {
      return 'Raw Compressor Outlet Temperature & Power';
    }
    const label = selectedStat?.label || '';
    // If label already contains "Compressor Inlet" or "Compressor Outlet", just add "Temperature"
    if (label.includes('Compressor Inlet') || label.includes('Compressor Outlet')) {
      return `${label} Temperature`;
    }
    // For backward compatibility with old stat types like 'min', 'max', 'median'
    return `${label} Compressor ${sensorType} Temperature`;
  };

  const option = {
    title: {
      show: false
    },
    legend: {
      data: isRawMode ? ['Temperature', 'Power'] : [`${selectedStat?.label} Temperature`],
      top: isMobile ? 0 : 10,
      left: 'center',
      itemGap: isMobile ? 6 : 20,
      textStyle: {
        fontSize: isMobile ? 8 : 12
      }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        if (isRawMode && Array.isArray(params)) {
          const time = new Date(params[0].data[0]).toLocaleString();
          let tooltip = `<b>Time:</b> ${time}<br/>`;
          params.forEach((param: any) => {
            if (param.seriesName === 'Temperature') {
              tooltip += `<b>Temperature:</b> ${param.data[1]?.toFixed(1) || 'N/A'}°F (${param.data[2]?.toFixed(1) || 'N/A'}°C)<br/>`;
            } else if (param.seriesName === 'Power') {
              tooltip += `<b>Power:</b> ${param.data[1]?.toFixed(1) || 'N/A'}W<br/>`;
            }
          });
          return tooltip;
        } else {
          const data = params[0];
          const date = new Date(data.data[0]);
          // For daily mode, show only date; for hourly mode, show date and time
          const timeString = aggregationType === 'daily' 
            ? date.toLocaleDateString()
            : date.toLocaleString();
          return `
            <b>${selectedStat?.label} Temperature</b><br/>
            Time: ${timeString}<br/>
            Temperature: ${data.data[1]?.toFixed(1) || 'N/A'}°F (${data.data[2]?.toFixed(1) || 'N/A'}°C)
          `;
        }
      }
    },
    grid: {
      left: isMobile ? '10%' : '3%',
      right: isMobile ? '8%' : '4%',
      bottom: isMobile ? '20%' : '3%',
      top: isMobile ? '25%' : '15%',
      containLabel: true
    },
    xAxis: {
      type: 'time',
      name: 'Time',
      nameLocation: 'middle',
      nameGap: isMobile ? 35 : 25,
      axisLabel: {
        rotate: isMobile ? 45 : 0,
        margin: isMobile ? 15 : 8,
        fontSize: isMobile ? 10 : 12,
        formatter: function(value: number) {
          if (isRawMode || aggregationType === 'hourly') {
            const date = new Date(value);
            if (isMobile) {
              // Shorter format for mobile
              return date.toLocaleDateString([], {month: 'short', day: 'numeric'}) + '\n' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
            }
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
          } else {
            const date = new Date(value);
            if (isMobile) {
              return date.toLocaleDateString([], {month: 'short', day: 'numeric'});
            }
            return date.toLocaleDateString();
          }
        }
      }
    },
    yAxis: isRawMode ? [
      {
        type: 'value',
        name: 'Temperature (°F)',
        nameLocation: 'middle',
        nameGap: isMobile ? 60 : 50,
        nameRotate: 90,
        position: 'left',
        min: minTemp - 10,
        max: maxTemp + 10,
        axisLabel: {
          fontSize: isMobile ? 10 : 12,
          formatter: function(value: number) {
            return value.toFixed(1);
          }
        }
      },
      {
        type: 'value',
        name: 'Power (W)',
        nameLocation: 'middle',
        nameGap: isMobile ? 60 : 50,
        nameRotate: -90,
        position: 'right',
        min: Math.max(0, minPower - 100),
        max: maxPower + 100,
        axisLabel: {
          fontSize: isMobile ? 10 : 12,
          formatter: function(value: number) {
            return value.toFixed(0);
          }
        }
      }
    ] : {
      type: 'value',
      name: 'Temperature (°F)',
      nameLocation: 'middle',
      nameGap: isMobile ? 50 : 40,
      nameRotate: 90,
      min: isInlet ? Math.min(minTemp - 10, FREEZING_TEMP_F - 5) : minTemp - 10, // Ensure freezing line is visible for inlet
      max: isInlet && selectedStatType.includes('min') ? Math.max(maxTemp + 10, 35) : maxTemp + 10, // For inlet min, ensure max is at least 35°F
      axisLabel: {
        fontSize: isMobile ? 10 : 12,
        formatter: function(value: number) {
          return value.toFixed(1);
        }
      }
    },
    series: [
      // Temperature series
      {
        name: isRawMode ? 'Temperature' : `${selectedStat?.label} Temperature`,
        type: 'line',
        yAxisIndex: 0,
        data: tempData.map(item => [item.x, item.y, item.temp_c, item.sensor_kit]),
        smooth: !isRawMode,
        symbol: 'circle',
        symbolSize: isRawMode ? 4 : 6,
        showSymbol: true,
        showAllSymbol: !isRawMode, // Show all symbols for aggregated (Athena) charts
        lineStyle: {
          width: isRawMode ? 1 : 2,
          color: selectedStat?.color || '#001F5C'
        },
        itemStyle: {
          color: selectedStat?.color || '#001F5C'
        },
        emphasis: {
          itemStyle: {
            color: selectedStat?.color || '#001F5C',
            borderColor: '#fff',
            borderWidth: 2,
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.3)'
          }
        }
      },
      // Power series (only in raw mode)
      ...(isRawMode ? [{
        name: 'Power',
        type: 'line',
        yAxisIndex: 1,
        data: powerData.map(item => [item.x, item.power_w]),
        smooth: false,
        symbol: 'circle',
        symbolSize: 4,
        lineStyle: {
          width: 1,
          color: '#10b981'
        },
        itemStyle: {
          color: '#10b981'
        },
        emphasis: {
          itemStyle: {
            color: '#10b981',
            borderColor: '#fff',
            borderWidth: 2,
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.3)'
          }
        }
      }] : []),
      // Warning Zone line (only for outlet charts, not inlet)
      ...(warningTempF && !isInlet ? [{
        name: 'Warning Zone',
        type: 'line',
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            position: 'insideStartTop',
            formatter: isMobile ? '' : 'Warning Zone',
            show: !isMobile
          },
          lineStyle: {
            color: '#f59e0b',
            type: 'dashed',
            width: 2
          },
          data: [
            {
              yAxis: warningTempF
            }
          ]
        }
      }] : []),
      // Critical Zone line (only for outlet charts, not inlet)
      ...(criticalTempF && !isInlet ? [{
        name: 'Critical Zone',
        type: 'line',
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            position: 'insideStartTop',
            formatter: isMobile ? '' : 'Critical Zone',
            show: !isMobile
          },
          lineStyle: {
            color: '#ef4444',
            type: 'dashed',
            width: 2
          },
          data: [
            {
              yAxis: criticalTempF
            }
          ]
        }
      }] : []),
      // Freezing line (only for inlet charts) - blue and dashed for both min and median
      ...(isInlet && !isRawMode ? [{
        name: 'Freezing (32°F)',
        type: 'line',
        markLine: {
          silent: true,
          symbol: 'none',
          label: {
            position: 'insideStartTop',
            formatter: 'Freezing (32°F)'
          },
          lineStyle: {
            color: '#3b82f6', // Blue for both min and median
            type: 'dashed',
            width: 2
          },
          data: [
            {
              yAxis: FREEZING_TEMP_F
            }
          ]
        }
      }] : []),
      // Detection time marker
      ...(highlightedDetectionTime ? [{
        name: 'Detection Time',
        type: 'line',
        markLine: {
          silent: true,
          symbol: ['none', 'arrow'],
          symbolSize: [0, 8],
          label: {
            position: 'end',
            formatter: 'Detection',
            color: '#9333ea',
            fontWeight: 'bold'
          },
          lineStyle: {
            color: '#9333ea',
            type: 'solid',
            width: 3
          },
          data: [
            {
              xAxis: highlightedDetectionTime.getTime()
            }
          ]
        }
      }] : [])
    ],
    dataZoom: isMobile ? [] : [
      {
        type: 'slider',
        start: 0,
        end: 100,
        bottom: 10,
        height: 20,
        handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23.1h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '80%',
        handleStyle: {
          color: '#fff',
          shadowBlur: 3,
          shadowColor: 'rgba(0, 0, 0, 0.6)',
          shadowOffsetX: 2,
          shadowOffsetY: 2
        }
      }
    ],
    animation: true,
    animationDuration: 1000
  };

  return (
    <div className="relative">
      {showDownloadButton && (
        <div className="absolute top-2 right-2 z-10">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="h-8 w-8 p-0"
            title="Download chart"
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      )}
      <ReactECharts 
        ref={chartRef}
        key={`chart-${selectedStatType}-${dataMode}`}
        option={option} 
        style={{ height: '300px', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
});

CompressorTempChartSimple.displayName = 'CompressorTempChartSimple';

export default CompressorTempChartSimple;
